import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";

export function useAccounts() {
  return useQuery({
    queryKey: ["accounts"],
    queryFn: async () => (await api.get("/accounts")).data,
  });
}

export function useAccount(accountId) {
  return useQuery({
    queryKey: ["account", accountId],
    queryFn: async () => (await api.get(`/accounts/${accountId}`)).data,
    enabled: !!accountId,
    staleTime: 60000,
  });
}

export function useAccountsSummary(shareToken) {
  return useQuery({
    queryKey: ["accounts-summary", shareToken || "me"],
    queryFn: async () =>
      (await api.get(shareToken ? `/shared/${shareToken}/accounts-summary` : "/accounts/summary")).data,
    staleTime: 120000,
  });
}

export function useAnalytics(accountId, range = "all", shareToken) {
  return useQuery({
    queryKey: ["analytics", accountId, range, shareToken || "me"],
    queryFn: async () =>
      (
        await api.get(
          shareToken
            ? `/shared/${shareToken}/analytics/${accountId}`
            : `/accounts/${accountId}/analytics`,
          { params: { range } }
        )
      ).data,
    enabled: !!accountId,
    staleTime: 120000,
    retry: 1,
  });
}

export function useTrades(accountId, page = 1, enabled = true) {
  return useQuery({
    queryKey: ["trades", accountId, page],
    queryFn: async () =>
      (await api.get(`/accounts/${accountId}/trades`, { params: { page, limit: 25 } })).data,
    enabled: !!accountId && enabled,
    staleTime: 120000,
  });
}

export function useJournal(shareToken) {
  return useQuery({
    queryKey: ["journal", shareToken || "me"],
    queryFn: async () =>
      (await api.get(shareToken ? `/shared/${shareToken}/journal` : "/journal")).data,
    staleTime: 30000,
  });
}

export function useVersions(accountId, shareToken) {
  return useQuery({
    queryKey: ["versions", accountId || "all", shareToken || "me"],
    queryFn: async () =>
      (
        await api.get(shareToken ? `/shared/${shareToken}/versions` : "/versions", {
          params: accountId ? { account_id: accountId } : {},
        })
      ).data,
    staleTime: 60000,
  });
}

export function useTemplates() {
  return useQuery({
    queryKey: ["templates"],
    queryFn: async () => (await api.get("/templates")).data,
    staleTime: Infinity,
  });
}

export function useMonthlyReturns(accountId, symbol = "ALL", shareToken) {
  return useQuery({
    queryKey: ["monthly-returns", accountId, symbol, shareToken || "me"],
    queryFn: async () =>
      (
        await api.get(
          shareToken ? `/shared/account/${shareToken}/monthly-returns` : `/accounts/${accountId}/monthly-returns`,
          { params: { symbol } }
        )
      ).data,
    enabled: !!accountId,
    staleTime: 60000,
  });
}

export function usePortfolios() {
  return useQuery({
    queryKey: ["portfolios"],
    queryFn: async () => (await api.get("/portfolios")).data,
    staleTime: 60000,
  });
}

export function usePortfolio(portfolioId) {
  return useQuery({
    queryKey: ["portfolio", portfolioId],
    queryFn: async () => (await api.get(`/portfolios/${portfolioId}`)).data,
    enabled: !!portfolioId,
    staleTime: 60000,
  });
}

export function usePortfolioAccounts(portfolioId) {
  return useQuery({
    queryKey: ["portfolio-accounts", portfolioId],
    queryFn: async () => (await api.get(`/portfolios/${portfolioId}/accounts`)).data,
    enabled: !!portfolioId,
    staleTime: 60000,
  });
}

export function useStrategyBreakdown(accountId, range, symbol) {
  return useQuery({
    queryKey: ["strategy-breakdown", accountId, range, symbol],
    queryFn: async () =>
      (await api.get(`/accounts/${accountId}/strategy-breakdown`, { params: { range, symbol } })).data,
    enabled: !!accountId,
    staleTime: 60000,
  });
}

export function useOverview(accountId, range, symbol, year, month, shareToken) {
  return useQuery({
    queryKey: ["overview", accountId, range, symbol, year, month, shareToken || "me"],
    queryFn: async () =>
      (
        await api.get(
          shareToken ? `/shared/account/${shareToken}/overview` : `/accounts/${accountId}/overview`,
          { params: { range, symbol, year, month } }
        )
      ).data,
    enabled: !!accountId,
    staleTime: 60000,
  });
}

export function useAdvancedStats(accountId, range = "all", symbol = "ALL", shareToken) {
  return useQuery({
    queryKey: ["advanced-stats", accountId, range, symbol, shareToken || "me"],
    queryFn: async () =>
      (
        await api.get(
          shareToken ? `/shared/account/${shareToken}/advanced-stats` : `/accounts/${accountId}/advanced-stats`,
          { params: { range, symbol } }
        )
      ).data,
    enabled: !!accountId,
    staleTime: 60000,
  });
}

export function useSharedAccount(token) {
  return useQuery({
    queryKey: ["shared-account", token],
    queryFn: async () => (await api.get(`/shared/account/${token}`)).data,
    enabled: !!token,
    retry: 1,
  });
}

export function useSharedPortfolio(token) {
  return useQuery({
    queryKey: ["shared-portfolio", token],
    queryFn: async () => (await api.get(`/shared/portfolio/${token}`)).data,
    enabled: !!token,
    retry: 1,
  });
}

export function useAccountShare(accountId) {
  return useQuery({
    queryKey: ["account-share", accountId],
    queryFn: async () => (await api.get(`/accounts/${accountId}/share`)).data,
    enabled: !!accountId,
  });
}

export function useOpenPositions(accountId, enabled = true) {
  return useQuery({
    queryKey: ["open-positions", accountId],
    queryFn: async () => (await api.get(`/accounts/${accountId}/open-positions`)).data,
    enabled: !!accountId && enabled,
    staleTime: 60000,
  });
}
